"use client";
import * as React from "react";
import { useState, useEffect } from "react";

type Keys = keyof typeof INITIAL_VALUES.samlConnection;
type Values = (typeof INITIAL_VALUES.samlConnection)[Keys];
import type {
  CreateConnectionProps,
  SAMLSSOConnection,
  SAMLSSORecord,
} from "../../types";
import { saveConnection } from "../../utils";
import defaultClasses from "./index.module.css";
import cssClassAssembler from "../../../utils/cssClassAssembler";
import Button from "../../../../shared/Button/index";
import Spacer from "../../../../shared/Spacer/index";
import Separator from "../../../../shared/Separator/index";
import Anchor from "../../../../shared/Anchor/index";
import Checkbox from "../../../../shared/Checkbox/index";
import InputField from "../../../../shared/inputs/InputField/index";
import TextArea from "../../../../shared/inputs/TextArea/index";
import Select from "../../../../shared/Select/index";
import ItemList from "../../../../shared/inputs/ItemList/index";
const DEFAULT_VALUES = {
  variant: "basic",
} satisfies Partial<CreateConnectionProps>;
const INITIAL_VALUES = {
  samlConnection: {
    name: "",
    label: "",
    description: "",
    tenant: "",
    product: "",
    redirectUrl: [""],
    defaultRedirectUrl: "",
    rawMetadata: "",
    metadataUrl: "",
    sortOrder: "" as unknown as string | number,
    forceAuthn: false as boolean,
    acsUrlOverride: "",
  },
};

function CreateSAMLConnection(props: CreateConnectionProps) {
  const [samlConnection, setSamlConnection] = useState(
    () => INITIAL_VALUES.samlConnection
  );

  function updateConnection(data: Partial<SAMLSSOConnection>) {
    return {
      ...samlConnection,
      ...data,
    };
  }

  const [isSaving, setIsSaving] = useState(() => false);

  function handleChange(event: Event) {
    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const id = target.id as Keys;
    const targetValue = (
      id !== "forceAuthn" ? target.value : (target as HTMLInputElement).checked
    ) as Values;
    setSamlConnection(
      updateConnection({
        [id]: targetValue,
      })
    );
  }

  function handleItemListUpdate(fieldName: string, listValue: string[]) {
    setSamlConnection(
      updateConnection({
        [fieldName]: listValue,
      })
    );
  }

  function save(event: Event) {
    event.preventDefault();
    setIsSaving(true);
    const { sortOrder, ...rest } = samlConnection;
    // pass sortOrder only if set to non-empty string
    const payload =
      sortOrder === ""
        ? rest
        : {
            ...samlConnection,
            sortOrder: +sortOrder,
          };
    saveConnection<SAMLSSORecord>({
      url: props.urls.post,
      formObj: payload,
      connectionIsSAML: true,
      callback: async (data) => {
        setIsSaving(false);
        if (data && typeof data === "object") {
          if ("error" in data) {
            typeof props.errorCallback === "function" &&
              props.errorCallback(data.error.message);
          } else {
            typeof props.successCallback === "function" &&
              props.successCallback({
                operation: "CREATE",
                connection: data,
                connectionIsSAML: true,
              });
          }
        }
      },
    });
  }

  function formVariant() {
    return props.variant || DEFAULT_VALUES.variant;
  }

  function classes() {
    return {
      form: cssClassAssembler(props.classNames?.form, defaultClasses.form),
      inputField: {
        label: props.classNames?.label,
        input: props.classNames?.input,
        container: props.classNames?.fieldContainer,
      },
      select: {
        label: props.classNames?.label,
        select: props.classNames?.select,
      },
      textarea: {
        label: props.classNames?.label,
        textarea: props.classNames?.textarea,
        container: props.classNames?.fieldContainer,
      },
    };
  }

  function isExcluded(fieldName: keyof SAMLSSOConnection) {
    return !!(props.excludeFields as (keyof SAMLSSOConnection)[])?.includes(
      fieldName
    );
  }

  function isReadOnly(fieldName: keyof SAMLSSOConnection) {
    if (
      fieldName === "tenant" &&
      Array.isArray(props.defaults?.tenant) &&
      props.defaults.tenant.length === 1
    ) {
      return true;
    }
    return !!(props.readOnlyFields as (keyof SAMLSSOConnection)[])?.includes(
      fieldName
    );
  }

  function isTenantADropdown() {
    return (
      Array.isArray(props.defaults?.tenant) && props.defaults.tenant.length > 1
    );
  }

  function tenantOptions() {
    return Array.isArray(props.defaults?.tenant)
      ? props.defaults?.tenant.map((tenant: string) => ({
          value: tenant,
          text: tenant,
        }))
      : [];
  }

  function shouldDisplayHeader() {
    if (props.displayHeader !== undefined) {
      return props.displayHeader;
    }
    return true;
  }

  useEffect(() => {
    if (props.defaults) {
      const _tenant = Array.isArray(props.defaults.tenant)
        ? props.defaults.tenant[0]
        : props.defaults.tenant;
      setSamlConnection(
        updateConnection({
          ...props.defaults,
          tenant: _tenant,
        })
      );
    }
  }, [props.defaults]);

  return (
    <div>
      {shouldDisplayHeader() ? (
        <h5 className={defaultClasses.h5}>Create SSO Connection</h5>
      ) : null}
      <form
        method="post"
        onSubmit={(event) => save(event)}
        className={classes().form}
      >
        {formVariant() === "advanced" ? (
          <>
            {!isExcluded("name") ? (
              <>
                <InputField
                  label="Connection name (Optional)"
                  id="name"
                  placeholder="MyApp"
                  classNames={classes().inputField}
                  required={false}
                  readOnly={isReadOnly("name")}
                  value={samlConnection.name}
                  handleInputChange={handleChange}
                />
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("label") ? (
              <>
                <InputField
                  label="Connection label (Optional)"
                  id="label"
                  placeholder="An internal label to identify the connection"
                  classNames={classes().inputField}
                  required={false}
                  readOnly={isReadOnly("label")}
                  value={samlConnection.label}
                  handleInputChange={handleChange}
                />
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("description") ? (
              <>
                <InputField
                  label="Description (Optional)"
                  id="description"
                  placeholder="A short description not more than 100 characters"
                  classNames={classes().inputField}
                  required={false}
                  readOnly={isReadOnly("description")}
                  maxLength={100}
                  value={samlConnection.description}
                  handleInputChange={handleChange}
                />
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("tenant") ? (
              <>
                {!isTenantADropdown() ? (
                  <InputField
                    label="Tenant"
                    id="tenant"
                    placeholder="acme.com"
                    aria-describedby="tenant-hint"
                    classNames={classes().inputField}
                    required
                    readOnly={isReadOnly("tenant")}
                    value={samlConnection.tenant}
                    handleInputChange={handleChange}
                  />
                ) : null}
                {isTenantADropdown() ? (
                  <div className={defaultClasses.selectContainer}>
                    <Select
                      label="Tenant"
                      name="tenant"
                      id="tenant"
                      options={tenantOptions()}
                      classNames={classes().select}
                      selectedValue={samlConnection.tenant}
                      handleChange={handleChange}
                    />
                  </div>
                ) : null}
                <div id="tenant-hint" className={defaultClasses.hint}>
                  Unique identifier for the tenant to which this SSO connection
                  is linked.See
                  <Spacer x={1} />
                  <Anchor
                    href="https://boxyhq.com/guides/jackson/configuring-saml-sso#sso-connection-identifier"
                    linkText="SSO connection identifier."
                  />
                </div>
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("product") ? (
              <>
                <InputField
                  label="Product"
                  id="product"
                  placeholder="demo"
                  aria-describedby="product-hint"
                  classNames={classes().inputField}
                  required
                  readOnly={isReadOnly("product")}
                  value={samlConnection.product}
                  handleInputChange={handleChange}
                />
                <div id="product-hint" className={defaultClasses.hint}>
                  Identifies the product/app to which this SSO connection is
                  linked.
                </div>
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("redirectUrl") ? (
              <>
                <ItemList
                  inputType="url"
                  label="Allowed redirect URLs"
                  fieldName="redirectUrl"
                  currentlist={samlConnection.redirectUrl}
                  handleItemListUpdate={handleItemListUpdate}
                  classNames={classes().inputField}
                />
                <div id="redirectUrl-hint" className={defaultClasses.hint}>
                  URL(s) to redirect the user to after login. Only the URLs in
                  this list are allowed in the OAuth flow.
                </div>
                <Spacer y={6} />
              </>
            ) : null}
            {!isExcluded("defaultRedirectUrl") ? (
              <>
                <InputField
                  label="Default redirect URL"
                  id="defaultRedirectUrl"
                  aria-describedby="defaultRedirectUrl-hint"
                  placeholder="http://localhost:3366/login/saml"
                  type="url"
                  classNames={classes().inputField}
                  required
                  readOnly={isReadOnly("defaultRedirectUrl")}
                  value={samlConnection.defaultRedirectUrl}
                  handleInputChange={handleChange}
                />
                <div
                  id="defaultRedirectUrl-hint"
                  className={defaultClasses.hint}
                >
                  URL to redirect the user to after an IdP initiated SAML login.
                </div>
              </>
            ) : null}
            <Spacer y={6} />
            <Separator text="SAML Provider Metadata" />
            <Spacer y={6} />
          </>
        ) : null}
        <TextArea
          label="Raw IdP XML"
          id="rawMetadata"
          aria-describedby="xml-metadata-hint"
          placeholder="Paste the raw XML here"
          classNames={classes().textarea}
          required={samlConnection.metadataUrl === ""}
          value={samlConnection.rawMetadata}
          handleInputChange={handleChange}
        />
        <div id="xml-metadata-hint" className={defaultClasses.hint}>
          Paste the raw XML metadata obtained from SAML provider or enter the
          metadata URL below.
        </div>
        <Spacer y={6} />
        <Separator text="OR" />
        <Spacer y={6} />
        <InputField
          label="Metadata URL"
          id="metadataUrl"
          type="url"
          placeholder="Paste the Metadata URL here"
          classNames={classes().inputField}
          required={samlConnection.rawMetadata === ""}
          value={samlConnection.metadataUrl}
          handleInputChange={handleChange}
        />
        <Spacer y={6} />
        {formVariant() === "advanced" ? (
          <>
            {!isExcluded("sortOrder") ? (
              <>
                <InputField
                  label="Sort Order"
                  id="sortOrder"
                  type="number"
                  min="0"
                  placeholder="10"
                  classNames={classes().inputField}
                  readOnly={isReadOnly("sortOrder")}
                  value={samlConnection.sortOrder as string}
                  handleInputChange={handleChange}
                />
                <div id="sortOrder-hint" className={defaultClasses.hint}>
                  Connections will be sorted (in a listing view like IdP
                  Selection) using this setting. Higher values will be displayed
                  first.
                </div>
              </>
            ) : null}
          </>
        ) : null}
        <Spacer y={6} />
        {formVariant() === "advanced" ? (
          <>
            {!isExcluded("acsUrlOverride") ? (
              <InputField
                label="ACS URL Override"
                id="acsUrlOverride"
                placeholder="https://yourcompany.com/app/saml/acs"
                type="url"
                classNames={classes().inputField}
                value={samlConnection.acsUrlOverride}
                handleInputChange={handleChange}
              />
            ) : null}
          </>
        ) : null}
        <Spacer y={6} />
        {formVariant() === "advanced" ? (
          <>
            {!isExcluded("forceAuthn") ? (
              <Checkbox
                label="Force Authentication"
                name="forceAuthn"
                id="forceAuthn"
                checked={samlConnection.forceAuthn}
                handleChange={handleChange}
              />
            ) : null}
          </>
        ) : null}
        <Spacer y={6} />
        <div className={defaultClasses.formAction}>
          {typeof props.cancelCallback === "function" ? (
            <Button
              type="button"
              name="Cancel"
              variant="outline"
              handleClick={props.cancelCallback}
              classNames={props.classNames?.button?.cancel}
            />
          ) : null}
          <Button
            type="submit"
            name="Save"
            classNames={props.classNames?.button?.ctoa}
            isLoading={isSaving}
          />
        </div>
      </form>
    </div>
  );
}

export default CreateSAMLConnection;
